"use client";

import { useLocale } from "next-intl";
import toast from "react-hot-toast";
import { Check } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import type { FormEvent } from "react";
import { useState } from "react";

interface NewsletterFormProps {
	className?: string;
}

function SubmitButton({ disabled, is_submitting }: { disabled?: boolean; is_submitting?: boolean }) {
	return (
		<button
			type="submit"
			disabled={is_submitting || disabled}
			className="h-10 w-24 hover:scale-105 hover:bg-ss-light-green/90 bg-ss-light-green text-black font-semibold rounded-full focus:outline-hidden disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-all duration-200 ease-in-out"
		>
			{is_submitting ? (
				<div className="flex items-center justify-center">
					<div className="flex space-x-1">
						<div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></div>
						<div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
						<div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
					</div>
				</div>
			) : (
				"Subscribe"
			)}
		</button>
	);
}

export function NewsletterForm({ className = "" }: NewsletterFormProps) {
	const locale = useLocale();
	const newsletter_email_input_id = "newsletter-email";

	const [email, set_email] = useState("");
	const [submitted, setSubmitted] = useState(false);
	const [is_already_subscribed, set_is_already_subscribed] = useState(false);

	const { mutate: subscribe_to_newsletter, isPending: is_submitting } = useMutation({
		mutationFn: async ({ email, locale }: { email: string; locale: string }) => {
			const response = await fetch("/api/newsletter", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({ email, locale }),
			});

			const result = await response.json().catch(() => null);
			if (!response.ok) {
				throw new Error(result?.error || "Failed to subscribe");
			}

			return result as { already_subscribed?: boolean };
		},
		onSuccess: (result) => {
			if (result?.already_subscribed) {
				toast.success("You are already subscribed to the newsletter.");
				set_is_already_subscribed(true);
			} else {
				toast.success("Successfully subscribed to newsletter!");
				set_is_already_subscribed(false);
			}
			setSubmitted(true);
		},
		onError: (error) => {
			console.error("Newsletter subscription error:", error);
			toast.error("Something went wrong. Please try again.");
		},
	});

	const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		const trimmed_email = email.trim();
		
		if (!trimmed_email || !trimmed_email.includes("@")) {
			toast.error("Please enter a valid email address");
			return;
		}

		subscribe_to_newsletter({ email: trimmed_email, locale });
	};

	return (
		<div className={className}>
			<h5 className="text-white text-xs mb-3  sm:text-left text-center">Subscribe to newsletter</h5>
			<div className="relative bg-white border border-gray-300 rounded-full overflow-hidden w-fit">
				{submitted ? (
					<div className="flex items-center justify-center gap-2 p-1 h-12 min-w-[284px]">
						<Check className="w-5 h-5 text-ss-light-green shrink-0" />
						<span className="text-sm text-black font-medium whitespace-nowrap">
							{is_already_subscribed ? "You are already subscribed" : "You have been subscribed"}
						</span>
					</div>
				) : (
					<form
						onSubmit={handleSubmit}
						className="flex justify-between p-1"
					>
						<label htmlFor={newsletter_email_input_id} className="sr-only">
							Email address
						</label>
						<input
							id={newsletter_email_input_id}
							name="email"
							type="email"
							value={email}
							onChange={(event) => set_email(event.target.value)}
							placeholder="Email"
							required
							autoComplete="email"
							className="h-10 w-[180px] px-5 outline-hidden text-black bg-transparent"
							style={{
								WebkitBoxShadow: '0 0 0 1000px white inset',
								WebkitTextFillColor: 'black'
							}}
						/>
						<SubmitButton disabled={submitted} is_submitting={is_submitting} />
					</form>
				)}
			</div>
		</div>
	);
}
